/**
 * AdminEventosMasivos.jsx · Agente A3 — Gestión de eventos masivos.
 * Lista + creación + cierre manual. Vista con mapa mostrando polígonos rojos
 * en zonas con evento activo.
 */
import { useState, useEffect, useRef } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  AlertOctagon, Plus, X, Save, Loader2, Clock, MapPin, Users,
  Building, XCircle, Zap, Music, Construction, MoreHorizontal, RefreshCw,
  Pentagon, UserX, Trash2,
} from "lucide-react";
import MassEventPolygonEditor from "../components/MassEventPolygonEditor";

const CENTER_ECUADOR = [-1.831239, -78.183406];
const DEFAULT_ZOOM = 7;
const CATEGORIES = [
  { key: "marcha", label: "Marcha / Protesta", icon: Users, color: "#DC2626" },
  { key: "concierto", label: "Concierto / Evento", icon: Music, color: "#8B5CF6" },
  { key: "corte_via", label: "Corte de vía", icon: Construction, color: "#EA580C" },
  { key: "otro", label: "Otro", icon: MoreHorizontal, color: "#4B5563" },
];

const catMeta = (k) => CATEGORIES.find(c => c.key === k) || CATEGORIES[3];

export default function AdminEventosMasivos() {
  const [tab, setTab] = useState("active");
  const [events, setEvents] = useState([]);
  const [zones, setZones] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showPolygon, setShowPolygon] = useState(false);
  const [showTechEvent, setShowTechEvent] = useState(false);
  const [techEvents, setTechEvents] = useState([]);
  // v3.1.2 · Buscador (P1 UX #18)
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [evR, znR, dbR, teR] = await Promise.allSettled([
        api.get(`/a3/events?status=${tab}`),
        api.get("/admin/zonas"),
        api.get("/a3/dashboard"),
        api.get("/v1/mass-events/tech-events"),
      ]);
      if (evR.status === "fulfilled") setEvents(evR.value.data.items || []);
      else toast.error("Error cargando eventos");
      if (znR.status === "fulfilled") setZones(znR.value.data || []);
      else toast.error("Error cargando zonas");
      if (dbR.status === "fulfilled") setDashboard(dbR.value.data);
      else toast.error("Error cargando dashboard A3");
      if (teR.status === "fulfilled") setTechEvents(teR.value.data.items || []);
    } catch (e) { toast.error("Error inesperado"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const closeEvent = async (id) => {
    const reason = window.prompt("Motivo del cierre manual:", "Situación normalizada");
    if (reason === null) return;
    try {
      const { data } = await api.post(`/a3/events/${id}/close`, { reason });
      toast.success(`Evento cerrado · ${data.affected_ot_count} OTs marcadas para reagendar`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al cerrar"); }
  };
  const deleteEvent = async (id) => {
    if (!window.confirm("¿Eliminar permanentemente este evento del historial?")) return;
    try { await api.delete(`/a3/events/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error("Error al eliminar"); }
  };

  const activeEvents = events.filter(e => e.status === "active");

  // v3.1.2 · Filtro por buscador (P1 UX #18)
  const filteredEvents = (() => {
    const t = q.trim().toLowerCase();
    if (!t) return events;
    return events.filter((ev) =>
      (ev.title || "").toLowerCase().includes(t) ||
      (ev.description || "").toLowerCase().includes(t) ||
      (ev.category || "").toLowerCase().includes(t) ||
      (ev.zone_name || "").toLowerCase().includes(t)
    );
  })();

  return (
    <Layout>
      <div className="space-y-4" data-testid="a3-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
                 style={{ background: "#FEE2E2", border: "1px solid #F87171" }}>
              <AlertOctagon size={11} className="text-red-700" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-red-700">Agente A3 · Bloqueo eventos masivos</span>
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#0A3747" }}>Eventos masivos</h1>
            <p className="mt-1 text-sm text-gray-500">Congela el agendamiento de OTs residenciales durante marchas, cortes o conciertos. Corporativas y emergencias siguen agendándose.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="neu-btn text-xs" data-testid="btn-refresh"><RefreshCw size={12}/> Refrescar</button>
            <button onClick={() => setShowTechEvent(true)} className="neu-btn text-xs" style={{ background: "#7C3AED", color: "#fff" }} data-testid="btn-new-tech-event">
              <UserX size={14}/> Nuevo por técnico
            </button>
            <button onClick={() => setShowPolygon(true)} className="neu-btn text-xs" style={{ background: "#0A3747", color: "#E0E621" }} data-testid="btn-new-polygon-event">
              <Pentagon size={14}/> Nuevo por polígono
            </button>
            <button onClick={() => setShowCreate(true)} className="neu-btn neu-btn-primary" data-testid="btn-new-event">
              <Plus size={14}/> Nuevo por zona
            </button>
          </div>
        </div>

        {/* KPIs */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <KPI label="Activos" value={dashboard.by_status?.active || 0} color="#DC2626" icon={AlertOctagon} />
            <KPI label="Cerrados manual" value={dashboard.by_status?.closed || 0} color="#059669" icon={XCircle} />
            <KPI label="Expirados auto" value={dashboard.by_status?.expired || 0} color="#F59E0B" icon={Clock} />
            <KPI label="OTs pending re-agenda" value={dashboard.pending_reschedule_ots} color="#7C3AED" icon={RefreshCw} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b">
          {[
            { key: "active", label: "Activos", n: dashboard?.by_status?.active || 0 },
            { key: "closed", label: "Cerrados", n: dashboard?.by_status?.closed || 0 },
            { key: "expired", label: "Expirados", n: dashboard?.by_status?.expired || 0 },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm font-semibold border-b-2 ${tab === t.key ? "border-[#0A3747] text-[#0A3747]" : "border-transparent text-gray-500"}`}
              data-testid={`tab-${t.key}`}>
              {t.label} <span className="text-[10px] opacity-60 ml-1">({t.n})</span>
            </button>
          ))}
        </div>

        {/* v3.1.2 · Buscador (P1 UX #18) */}
        <div className="flex items-center gap-2 -mt-1">
          <div className="relative flex-1 max-w-md">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="🔍 Buscar por título, descripción, zona o categoría…"
              className="w-full text-xs px-3 py-1.5 border rounded pl-8"
              data-testid="events-search"
            />
            <span className="absolute left-2 top-2 text-gray-400 text-[11px]">🔎</span>
          </div>
          {q && <span className="text-[10px] text-gray-500">{filteredEvents.length} coincidencias</span>}
        </div>

        {/* Layout: lista + mapa */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-2">
            {loading ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-gray-400"/></div>
            : filteredEvents.length === 0 ? <div className="neu-card p-10 text-center text-sm text-gray-500">{q ? "Sin resultados para la búsqueda." : "Sin eventos en esta pestaña."}</div>
            : filteredEvents.map(ev => (
              <EventCard key={ev.id} event={ev} onClose={() => closeEvent(ev.id)} onDelete={() => deleteEvent(ev.id)} />
            ))}
          </div>

          {/* Mapa */}
          <div className="neu-card overflow-hidden" style={{ height: 500 }}>
            <MapContainer center={CENTER_ECUADOR} zoom={DEFAULT_ZOOM} style={{ height: "100%", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
              <EventOverlays events={activeEvents} zones={zones} />
            </MapContainer>
          </div>
        </div>
        {/* ═══ Eventos por Técnico (v92.4.0) ═══ */}
        {techEvents.length > 0 && (
          <div className="space-y-2">
            <h2 className="text-lg font-bold flex items-center gap-2" style={{ color: "#0A3747" }}>
              <UserX size={18} className="text-purple-600"/> Bloqueos por técnico
              <span className="text-[10px] font-normal text-gray-500 ml-1">({techEvents.filter(te => te.status === "active").length} activos)</span>
            </h2>
            {techEvents.map(te => (
              <TechEventCard key={te.id} event={te} onDelete={async () => {
                if (!window.confirm("¿Eliminar este bloqueo de técnico?")) return;
                try { await api.delete(`/v1/mass-events/tech-events/${te.id}`); toast.success("Eliminado"); load(); }
                catch (e) { toast.error("Error al eliminar"); }
              }}/>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateEventModal zones={zones} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />
      )}
      {showTechEvent && (
        <TechEventModal onClose={() => setShowTechEvent(false)} onSaved={() => { setShowTechEvent(false); load(); }} />
      )}
      <MassEventPolygonEditor
        open={showPolygon}
        onClose={() => setShowPolygon(false)}
        onCreated={() => { setShowPolygon(false); load(); }}
      />
    </Layout>
  );
}

function KPI({ label, value, color, icon: Icon }) {
  return (
    <div className="neu-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>
        <Icon size={12}/> {label}
      </div>
      <div className="text-3xl font-bold mt-1 tabular-nums" style={{ color: "#0A3747" }}>{value}</div>
    </div>
  );
}

function EventCard({ event, onClose, onDelete }) {
  const cat = catMeta(event.category);
  const isActive = event.status === "active";
  const bgTint = { active: "#FEE2E2", closed: "#D1FAE5", expired: "#FEF3C7" }[event.status];
  const bdColor = { active: "#F87171", closed: "#6EE7B7", expired: "#FCD34D" }[event.status];
  const fmt = (s) => s ? new Date(s).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" }) : "—";
  return (
    <div className="rounded-xl border-2 p-4" style={{ background: bgTint, borderColor: bdColor }} data-testid={`event-card-${event.id}`}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg" style={{ background: cat.color + "20", color: cat.color }}>
          <cat.icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-base" style={{ color: "#0A3747" }}>{event.title}</h3>
            <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded" style={{ background: cat.color, color: "#fff" }}>{cat.label}</span>
          </div>
          <div className="mt-1 text-xs text-gray-600 flex items-center flex-wrap gap-x-4 gap-y-0.5">
            <span className="flex items-center gap-1"><MapPin size={11}/> {event.zone_name}</span>
            <span className="flex items-center gap-1"><Clock size={11}/> {fmt(event.starts_at)} → {fmt(event.ends_at)}</span>
            {event.affected_ot_count > 0 && (
              <span className="flex items-center gap-1"><RefreshCw size={11}/> {event.affected_ot_count} OTs afectadas</span>
            )}
          </div>
          {event.description && <div className="mt-1.5 text-xs text-gray-700 italic">{event.description}</div>}
          {event.close_reason && (
            <div className="mt-1.5 text-[11px] text-gray-600">
              <b>Cierre:</b> {event.close_reason} <span className="opacity-60">· {fmt(event.closed_at)} · {event.closed_by}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          {isActive ? (
            <button onClick={onClose} className="neu-btn text-xs" data-testid={`close-event-${event.id}`}>
              <XCircle size={12}/> Cerrar
            </button>
          ) : (
            <button onClick={onDelete} className="text-xs text-red-600 opacity-70 hover:opacity-100" data-testid={`delete-event-${event.id}`}>
              <X size={12}/> Eliminar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function EventOverlays({ events, zones }) {
  const map = useMap();
  const layersRef = useRef(new Map());
  useEffect(() => {
    // Limpiar anteriores
    for (const l of layersRef.current.values()) { try { map.removeLayer(l); } catch (_) { /* noop */ } }
    layersRef.current.clear();
    // Añadir capas nuevas (una por evento activo)
    events.forEach(ev => {
      const zone = zones.find(z => z.id === ev.zone_id);
      if (!zone || !zone.polygon || zone.polygon.length < 3) return;
      const cat = catMeta(ev.category);
      const poly = L.polygon(zone.polygon, {
        color: cat.color, weight: 3, fillColor: cat.color, fillOpacity: 0.35,
        dashArray: "8, 8",
      });
      poly.bindPopup(`
        <div style="min-width:180px">
          <div style="font-weight:800;color:${cat.color};font-size:13px">🚧 ${ev.title}</div>
          <div style="font-size:11px;color:#666">${cat.label}</div>
          <div style="font-size:11px;color:#666;margin-top:4px">
            <b>Zona:</b> ${zone.name}<br>
            <b>Ventana:</b> ${new Date(ev.starts_at).toLocaleString("es-EC",{timeZone: "America/Guayaquil", dateStyle:"short",timeStyle:"short"})}<br>
            → ${new Date(ev.ends_at).toLocaleString("es-EC",{timeZone: "America/Guayaquil", dateStyle:"short",timeStyle:"short"})}
          </div>
        </div>
      `);
      poly.addTo(map);
      layersRef.current.set(ev.id, poly);
    });
    // Fit bounds si hay eventos
    if (events.length > 0 && layersRef.current.size > 0) {
      const group = L.featureGroup(Array.from(layersRef.current.values()));
      try { map.fitBounds(group.getBounds(), { padding: [30, 30], maxZoom: 12 }); }
      catch (_) { /* noop */ }
    }
    return () => {
      for (const l of layersRef.current.values()) { try { map.removeLayer(l); } catch (_) { /* noop */ } }
    };
  }, [events, zones, map]);
  return null;
}

function CreateEventModal({ zones, onClose, onSaved }) {
  const [zoneId, setZoneId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("marcha");
  const [startsAt, setStartsAt] = useState(toLocalInput(new Date()));
  const [endsAt, setEndsAt] = useState(toLocalInput(new Date(Date.now() + 4 * 3600 * 1000)));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!zoneId || !title.trim()) { toast.error("Zona y título son requeridos"); return; }
    setSaving(true);
    try {
      await api.post("/a3/events", {
        zone_id: zoneId, title: title.trim(), description, category,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
      });
      toast.success("Evento masivo creado · Bloqueo activo");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="create-event-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full flex flex-col shadow-2xl">
        <div className="flex items-start justify-between p-5 border-b" style={{ background: "#FEE2E2" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-red-700">Nuevo evento masivo</div>
            <h2 className="text-xl font-bold mt-1" style={{ color: "#0A3747" }}>Bloquear zona</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="modal-close"><X size={22}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Zona afectada *</label>
            <select value={zoneId} onChange={(e) => setZoneId(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm" data-testid="ev-zone">
              <option value="">— Selecciona zona —</option>
              {zones.filter(z => z.active !== false).map(z => (
                <option key={z.id} value={z.id}>{z.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Categoría *</label>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map(c => (
                <button key={c.key} onClick={() => setCategory(c.key)}
                        className={`flex items-center gap-2 px-3 py-2 rounded border text-sm ${category === c.key ? "border-2 font-semibold" : "border-gray-300"}`}
                        style={category === c.key ? { borderColor: c.color, background: c.color + "15", color: c.color } : {}}
                        data-testid={`ev-cat-${c.key}`}>
                  <c.icon size={14}/> {c.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Título *</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="Ej: Marcha 24J Av. Amazonas"
                   className="w-full px-3 py-2 border rounded text-sm" data-testid="ev-title"/>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Descripción</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                     rows="2" placeholder="Detalles opcionales"
                     className="w-full px-3 py-2 border rounded text-sm" data-testid="ev-desc"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Inicio *</label>
              <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)}
                     className="w-full px-3 py-2 border rounded text-sm" data-testid="ev-start"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Fin *</label>
              <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)}
                     className="w-full px-3 py-2 border rounded text-sm" data-testid="ev-end"/>
            </div>
          </div>
          <div className="text-[10px] text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
            <b>Regla:</b> Duración máxima 3 días. El sistema cerrará automáticamente si el agendador no lo cierra manualmente al vencerse. Las OTs residenciales de esta zona quedarán congeladas mientras el evento esté activo.
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="neu-btn" data-testid="ev-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="neu-btn neu-btn-primary" data-testid="ev-save">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Crear bloqueo
          </button>
        </div>
      </div>
    </div>
  );
}

function toLocalInput(d) {
  // Convierte Date a formato YYYY-MM-DDTHH:MM para <input type="datetime-local">
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * TechEventCard · v92.4.0 — Tarjeta de bloqueo por técnico
 * ═══════════════════════════════════════════════════════════════════════════ */
function TechEventCard({ event: te, onDelete }) {
  const isActive = te.status === "active";
  return (
    <div className="neu-card p-3 flex items-center justify-between gap-3 border-l-4"
         style={{ borderLeftColor: isActive ? "#7C3AED" : "#9CA3AF" }}
         data-testid={`tech-event-${te.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${isActive ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-500"}`}>
            {isActive ? "Activo" : "Expirado"}
          </span>
          <span className="font-semibold text-sm truncate">{te.motivo}</span>
        </div>
        <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
          <span><Clock size={10} className="inline mr-0.5"/> {new Date(te.desde).toLocaleDateString("es")} — {new Date(te.hasta).toLocaleDateString("es")}</span>
          <span><Users size={10} className="inline mr-0.5"/> {(te.tecnico_names || []).join(", ")}</span>
        </div>
        {te.nota && <p className="text-[11px] text-gray-400 mt-0.5 truncate">{te.nota}</p>}
      </div>
      <button onClick={onDelete} className="neu-btn text-xs text-red-600 hover:bg-red-50" data-testid={`del-tech-event-${te.id}`}>
        <Trash2 size={13}/>
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * TechEventModal · v92.4.0 — Modal para crear evento por técnico
 * ═══════════════════════════════════════════════════════════════════════════ */
function TechEventModal({ onClose, onSaved }) {
  const [techs, setTechs] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [motivo, setMotivo] = useState("Calamidad");
  const [nota, setNota] = useState("");
  const [desde, setDesde] = useState(toLocalInput(new Date()));
  const [hasta, setHasta] = useState(toLocalInput(new Date(Date.now() + 86400000 * 3)));
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  // v92.6.9 · Técnicos desde /ot_smart/technicians (accesible al agendador; /v1/users exige permiso de usuarios)
  // v101.6.0 · Sucursales desde /config/branches (UUID Alfafield); filter-options usa branch_office_id Odoo y no matchea
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  useEffect(() => {
    api.get("/v1/ot_smart/technicians").then(r => {
      setTechs(r.data?.items || []);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Error cargando técnicos"));
    api.get("/v1/config/branches").then(r => {
      setBranches(r.data?.items || []);
    }).catch(() => {});
  }, []);
  const branchName = (tOrId) => {
    // v101.6.0 · Acepta objeto técnico (con branch_name) o id crudo
    if (tOrId && typeof tOrId === "object") {
      if (tOrId.branch_name) return tOrId.branch_name;
      tOrId = tOrId.branch_id;
    }
    const id = tOrId;
    const b = branches.find(x => String(x.id) === String(id));
    return b ? (b.name || b.nomenclatura || b.code) : (id ? "Sucursal sin nombre" : "Sin sucursal");
  };
  const techLabel = (t) => t.name || t.full_name || t.email || "Sin nombre";

  const toggleTech = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const filteredTechs = techs.filter(t =>
    (!branchFilter || String(t.branch_id) === String(branchFilter)) && (
    (t.name || t.full_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (t.email || "").toLowerCase().includes(search.toLowerCase()))
  );

  const save = async () => {
    if (!selectedIds.length) { toast.error("Selecciona al menos un técnico"); return; }
    if (!motivo.trim()) { toast.error("Motivo requerido"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/v1/mass-events/tech-events", {
        tecnico_ids: selectedIds,
        motivo: motivo.trim(),
        desde: new Date(desde).toISOString(),
        hasta: new Date(hasta).toISOString(),
        nota: nota.trim(),
      });
      toast.success(`Bloqueo creado · ${data.tecnico_count} técnico(s) · ${data.affected_ot_count} OTs desasignadas`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando bloqueo");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="tech-event-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between" style={{ background: "#7C3AED10" }}>
          <div>
            <h3 className="font-bold text-base" style={{ color: "#0A3747" }}>Bloquear técnico(s)</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">Calamidad, enfermedad, permiso. Las OTs asignadas en la ventana serán desasignadas.</p>
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {/* Motivo */}
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Motivo *</label>
            <select value={motivo} onChange={e => setMotivo(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm" data-testid="te-motivo">
              <option>Calamidad</option>
              <option>Enfermedad</option>
              <option>Permiso</option>
              <option>Vacaciones</option>
              <option>Otro</option>
            </select>
          </div>
          {/* Fechas */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Desde *</label>
              <input type="datetime-local" value={desde} onChange={e => setDesde(e.target.value)}
                     className="w-full px-3 py-2 border rounded text-sm" data-testid="te-desde"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Hasta *</label>
              <input type="datetime-local" value={hasta} onChange={e => setHasta(e.target.value)}
                     className="w-full px-3 py-2 border rounded text-sm" data-testid="te-hasta"/>
            </div>
          </div>
          {/* Nota */}
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Nota</label>
            <input type="text" value={nota} onChange={e => setNota(e.target.value)} maxLength={500}
                   placeholder="Detalle opcional" className="w-full px-3 py-2 border rounded text-sm" data-testid="te-nota"/>
          </div>
          {/* Selector de técnicos */}
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">
              Técnicos * <span className="text-purple-600 font-normal">({selectedIds.length} seleccionado{selectedIds.length !== 1 ? "s" : ""})</span>
            </label>
            <div className="flex gap-2 mb-2">
              <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)}
                      className="px-2 py-2 border rounded text-sm" data-testid="te-branch-filter">
                <option value="">Todas las sucursales</option>
                {[...new Set(techs.map(t => t.branch_id).filter(Boolean))].map(id => (
                  <option key={id} value={id}>{branchName(id)}</option>
                ))}
              </select>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                     placeholder="Buscar por nombre o email..." className="flex-1 px-3 py-2 border rounded text-sm" data-testid="te-search-techs"/>
            </div>
            <div className="border rounded max-h-40 overflow-y-auto divide-y" data-testid="te-tech-list">
              {filteredTechs.length === 0 && <p className="p-3 text-xs text-gray-400 text-center">Sin técnicos disponibles</p>}
              {filteredTechs.map(t => (
                <label key={t.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-purple-50 text-sm ${selectedIds.includes(t.id) ? "bg-purple-50" : ""}`}>
                  <input type="checkbox" checked={selectedIds.includes(t.id)} onChange={() => toggleTech(t.id)}
                         className="accent-purple-600" data-testid={`te-check-${t.id}`}/>
                  {/* v101.6.0 · name puede venir vacío; no mostrar UUID de sucursal como etiqueta principal */}
                  <span className="truncate flex-1">{techLabel(t)}</span>
                  <span className="text-[10px] text-purple-700 shrink-0">{branchName(t)}</span>
                  {techLabel(t) !== t.email && (
                    <span className="text-[10px] text-gray-400 truncate max-w-[140px]">{t.email}</span>
                  )}
                </label>
              ))}
            </div>
          </div>
          <div className="text-[10px] text-purple-700 bg-purple-50 p-2 rounded border border-purple-200">
            <b>Efecto:</b> Todas las OTs asignadas a estos técnicos en la ventana serán desasignadas automáticamente y marcadas para reagendar.
            El agendamiento automático (A2) excluirá a estos técnicos mientras dure el bloqueo.
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="neu-btn" data-testid="te-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="neu-btn" data-testid="te-save"
                  style={{ background: "#7C3AED", color: "#fff" }}>
            {saving ? <Loader2 size={14} className="animate-spin"/> : <UserX size={14}/>} Crear bloqueo
          </button>
        </div>
      </div>
    </div>
  );
}
